import {
  Control,
  Controller,
  FieldErrors,
  get,
  useFieldArray,
} from 'react-hook-form';

import { withMask } from 'use-mask-input';

import { IcDeleteTrash } from '@/assets/images';
import { AddressForm } from '@/components';
import { renderRequiredLabel } from '@/components/FormElements/RequiredFieldLabel';
import {
  Button,
  Input,
  mapToSelectOptions,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SSNInput,
  Textarea,
} from '@/components/TailwindComponents';
import { AsyncSelect } from '@/components/TailwindComponents/components/AsyncSelect/AsyncSelect';
import { MultiselectCombobox } from '@/components/TailwindComponents/components/Form/MultiselectCombobox';
import { NumberInput } from '@/components/TailwindComponents/components/Number';
import { cn } from '@/components/TailwindComponents/lib/utils';
import { IFile, PhoneNumberType } from '@/models';

import { FilesViewAndUpload } from './FilesViewAndUpload';
import { IJMSFormFieldProps } from '../../data/data';

interface IRenderFormFieldProps {
  field: IJMSFormFieldProps;
  control: Control<any>;
  errors: FieldErrors<any>;
  name?: string;
  defaultValue?: any;
  handleChange?: (name: string, value: any) => void;
  handleError?: (name: string, message: string) => void;
  getOptionItems?: { [key: string]: any };
  getItemAction?: { [key: string]: any };
  handleItemAction?: { [key: string]: any };
}

export const renderFormField = ({
  field,
  control,
  errors,
  name = '',
  handleChange,
  handleError,
  getOptionItems = {},
  getItemAction = {},
  handleItemAction = {},
}: IRenderFormFieldProps) => {
  const getOptions = (nameLoc: string): any => {
    return getOptionItems[nameLoc];
  };

  const getAction = (action: string) => {
    return getItemAction[action];
  };

  if (field.type === 'heightExactInput') {
    const feetFieldName = `${field.name}.feet`;
    const inchesFieldName = `${field.name}.inches`;
    const feetError = get(errors, feetFieldName);
    const inchesError = get(errors, inchesFieldName);

    return (
      <div
        key={`${name}-${field.name}`}
        className={`flex flex-col gap-0.5 ${field.className}`}
      >
        <label
          htmlFor={feetFieldName}
          className="text-sm font-medium text-gray-800 dark:text-gray-100"
        >
          {renderRequiredLabel(field.label, field.required)}
        </label>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-0.5">
            <label
              htmlFor={feetFieldName}
              className="text-xs font-medium text-gray-600 dark:text-gray-400"
            >
              Feet
            </label>
            <Controller
              name={feetFieldName}
              control={control}
              render={({ field: feetInputField }) => (
                <NumberInput
                  id={feetFieldName}
                  disabled={field?.disabled || false}
                  value={feetInputField.value ?? ''}
                  onChange={feetInputField.onChange}
                  className="w-full"
                  hideSpinner={field.hideSpinner}
                  step="1"
                />
              )}
            />
          </div>
          <div className="flex flex-col gap-0.5">
            <label
              htmlFor={inchesFieldName}
              className="text-xs font-medium text-gray-600 dark:text-gray-400"
            >
              Inches
            </label>
            <Controller
              name={inchesFieldName}
              control={control}
              render={({ field: inchesInputField }) => (
                <NumberInput
                  id={inchesFieldName}
                  disabled={field?.disabled || false}
                  value={inchesInputField.value ?? ''}
                  onChange={inchesInputField.onChange}
                  className="w-full"
                  hideSpinner={field.hideSpinner}
                  step="0.01"
                />
              )}
            />
          </div>
        </div>
        {feetError && (
          <div className="text-sm text-red-500">
            {String(feetError.message)}
          </div>
        )}
        {inchesError && (
          <div className="text-sm text-red-500">
            {String(inchesError.message)}
          </div>
        )}
      </div>
    );
  }

  if (field.multiple && field.type !== 'file') {
    return (
      <MultipleFieldRenderer
        key={`${name}-${field.name}`}
        field={field}
        control={control}
        errors={errors}
        name={field.name}
      />
    );
  }

  return (
    <div
      key={`${name}-${field.name}`}
      className={`flex flex-col gap-0.5 ${field.className}`}
    >
      <Controller
        name={field.name}
        control={control}
        render={({ field: { onChange, value } }) => {
          switch (field.type) {
            case 'address':
              return (
                <AddressForm
                  onChange={onChange}
                  name={field.name}
                  value={value}
                  disabled={field?.disabled || false}
                />
              );
            case 'select':
              return (
                <div className="flex flex-col gap-0.5 relative">
                  <label
                    htmlFor={field.name}
                    className="text-sm font-medium text-gray-800 dark:text-gray-100"
                  >
                    {renderRequiredLabel(field.label, field.required)}
                  </label>
                  <Select
                    onValueChange={(selectedValue) => {
                      if (value === String(selectedValue)) {
                        onChange(null);
                      } else {
                        onChange(selectedValue);
                      }
                    }}
                    value={value ? String(value) : ''}
                    disabled={field?.disabled || false}
                  >
                    <SelectTrigger className="w-full">
                      <div
                        className={cn(
                          'truncate',
                          value && !field.hideClear && 'pr-6',
                        )}
                      >
                        <SelectValue placeholder={field.placeholder} />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {(field.items
                        ? typeof field.items === 'string' &&
                          field.items in getOptionItems
                          ? getOptions(field.items as string)
                          : Array.isArray(field?.items)
                          ? field.items
                          : []
                        : []
                      ).map(
                        ({
                          label,
                          value: val,
                        }: {
                          label: string;
                          value: { _id: string } | string;
                        }) => {
                          if ((val as { _id: string })._id) {
                            return (
                              <SelectItem
                                key={(val as { _id: string })._id}
                                value={String((val as { _id: string })._id)}
                              >
                                {label}
                              </SelectItem>
                            );
                          }
                          return (
                            <SelectItem
                              key={val as string}
                              value={String(val as string)}
                            >
                              {label}
                            </SelectItem>
                          );
                        },
                      )}
                    </SelectContent>
                  </Select>
                  {value && !field.hideClear && !field.disabled && (
                    <button
                      type="button"
                      onClick={() => onChange(null)}
                      className="absolute right-6 top-6 px-2 py-1 text-[14px] text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      ✕
                    </button>
                  )}
                </div>
              );
            case 'asyncSelect':
              return (
                <AsyncSelect
                  fetchOptions={getOptions(field.items as string)}
                  onSelect={(
                    option: { value: string; label: string; id: string } | null,
                  ) => {
                    onChange(option ? option.id : null);
                  }}
                  label={field.label}
                  debounceMs={300}
                  className="w-full"
                  value={value}
                  disabled={field.disabled}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.id}
                  itemAction={getAction(field.itemAction as string)}
                  onItemAction={handleItemAction[field.itemAction as string]}
                />
              );
            case 'checkbox':
              return (
                <div className="flex items-center gap-3 w-full">
                  <Input
                    type="checkbox"
                    id={field.name}
                    checked={value as boolean}
                    onChange={onChange}
                    color="error"
                    disabled={field?.disabled || false}
                    className="w-4 h-4 rounded-md text-red-500 border-gray-300 bg-gray-100 border focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <label
                    htmlFor={field.name}
                    className="text-sm font-medium text-gray-600 dark:text-gray-400"
                  >
                    {renderRequiredLabel(field.label, field.required)}
                  </label>
                </div>
              );
            case 'file':
              return (
                <FilesViewAndUpload
                  value={value}
                  label={
                    renderRequiredLabel(field.label, field.required) as string
                  }
                  tab={field.tab}
                  disabledDelete={field.disabledDelete}
                  handleUpload={(files: IFile[]) => {
                    if (handleChange) handleChange(field.name, files);
                    onChange([...files]);
                  }}
                  allowedFileTypes={field.accept}
                  handleError={(message) => {
                    if (handleError) handleError(field.name, message);
                  }}
                  disabled={field?.disabled || false}
                  isModal={field?.isModal || false}
                />
              );
            case 'ssn':
              return (
                <SSNInput
                  field={field}
                  value={value}
                  onChange={onChange}
                  disabled={field?.disabled || false}
                />
              );
            case 'numberInput':
              return (
                <div className="flex flex-col gap-0.5">
                  <label
                    htmlFor={field.name}
                    className="text-sm font-medium text-gray-800 dark:text-gray-100"
                  >
                    {renderRequiredLabel(field.label, field.required)}
                  </label>
                  <NumberInput
                    {...field}
                    ref={
                      field.format
                        ? withMask(field.format, {
                            autoUnmask: true,
                          })
                        : null
                    }
                    disabled={field?.disabled || false}
                    value={value as string | number}
                    onChange={onChange}
                    className="w-full"
                    hideSpinner={field.hideSpinner}
                  />
                </div>
              );
            case 'textarea':
              return (
                <div className="flex flex-col gap-0.5">
                  <label
                    htmlFor={field.name}
                    className="text-sm font-medium text-gray-800 dark:text-gray-100"
                  >
                    {renderRequiredLabel(field.label, field.required)}
                  </label>
                  <Textarea
                    ref={
                      field.format
                        ? withMask(field.format, {
                            autoUnmask: true,
                          })
                        : null
                    }
                    disabled={field?.disabled || false}
                    value={value as string | number}
                    onChange={onChange}
                    className="w-full"
                  />
                </div>
              );
            case 'phoneNumber':
              return (
                <div className="flex flex-col gap-0.5">
                  <label
                    htmlFor={field.name}
                    className="text-sm font-medium text-gray-800 dark:text-gray-100"
                  >
                    {renderRequiredLabel(field.label, field.required)}
                  </label>
                  <Input
                    {...field}
                    disabled={field?.disabled || false}
                    ref={
                      field.format
                        ? withMask(field.format, {
                            autoUnmask: true,
                          })
                        : null
                    }
                    type={field.type}
                    value={value?.replace(/^\+1/, '')}
                    onChange={(e) =>
                      onChange(
                        e.target.value
                          ? `+1${e.target.value.replace(/\D/g, '')}`
                          : null,
                      )
                    }
                    className="w-full"
                  />
                </div>
              );
            default:
              return (
                <div className="flex flex-col gap-0.5">
                  <label
                    htmlFor={field.name}
                    className="text-sm font-medium text-gray-800 dark:text-gray-100"
                  >
                    {renderRequiredLabel(field.label, field.required)}
                  </label>
                  <Input
                    {...field}
                    ref={
                      field.format
                        ? withMask(field.format, {
                            autoUnmask: true,
                          })
                        : null
                    }
                    disabled={field?.disabled || false}
                    type={field.type}
                    value={value as string | number}
                    onChange={onChange}
                    className="w-full"
                  />
                </div>
              );
          }
        }}
      />
      {get(errors, field.name) && (
        <div className="text-sm text-red-500">
          {String(get(errors, field.name)?.message)}
        </div>
      )}
    </div>
  );
};

export const MultipleFieldRenderer = ({
  field,
  control,
  errors,
  name,
}: {
  field: IJMSFormFieldProps;
  control: Control<any>;
  errors: FieldErrors<any>;
  name: string;
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  const getDefaultValue = () => {
    switch (field.type) {
      case 'checkbox':
        return false;
      case 'number':
        return 0;
      case 'select':
        return '';
      case 'phoneNumber':
        return { type: PhoneNumberType.OTHER, phoneNumber: '' };
      case 'address':
        return {};
      default:
        return '';
    }
  };

  const getOptionItems: { [key: string]: any } = {
    getUsers: [],
    getCases: [],
    getAgencies: [],
  };
  const getOptions = (nameLoc: string): { label: string; value: string }[] => {
    return getOptionItems[nameLoc];
  };

  if (field.type === 'select') {
    return (
      <div className={`flex flex-col gap-0.5 ${field.className}`}>
        <label
          htmlFor={field.name}
          className="text-sm font-medium text-gray-800 dark:text-gray-100"
        >
          {renderRequiredLabel(field.label, field.required)}
        </label>
        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, value } }) => {
            return (
              <MultiselectCombobox
                classNames="w-full"
                emptyResultsMessage={`No ${field.label} found.`}
                onSelect={onChange}
                options={mapToSelectOptions(
                  field.items
                    ? typeof field.items === 'string' &&
                      field.items in getOptionItems
                      ? getOptions(field.items as string)
                      : Array.isArray(field?.items)
                      ? field.items
                      : []
                    : [],
                  (item) => ({
                    label: item.label,
                    value: item.value as string,
                  }),
                )}
                value={value as string[]}
                placeholder={field.placeholder}
                searchPlaceholder={`Search ${field.label}...`}
                showSelectedItems={false}
                customSelectListFormatter={(items) => items.join(', ')}
                disabled={field?.disabled || false}
              />
            );
          }}
        />
        {get(errors, name) && (
          <div className="text-sm text-red-500">
            {String(get(errors, name)?.message) ||
              String(get(errors, name)?.[0]?.message)}
          </div>
        )}
      </div>
    );
  }

  // For other multiple fields (text, number, etc.)
  return (
    <div className={`flex flex-col gap-2 ${field.className}`}>
      {fields.map((item, index) => (
        <div key={item.id} className="flex gap-2">
          <Controller
            name={`${name}.${index}${
              field.objectField ? `.${field.objectField}` : ''
            }`}
            control={control}
            render={({ field: { onChange, value } }) => {
              switch (field.type) {
                case 'address':
                  return (
                    <div className="w-full text-sm text-gray-900 dark:text-gray-100">
                      <AddressForm
                        disabled={field?.disabled || false}
                        onChange={onChange}
                        value={value}
                      />
                      <div className="text-sm text-red-500">
                        {get(errors, name)?.message}
                        {get(errors, name)?.[index]?.addressLabel?.message
                          ? get(errors, name)?.[index]?.addressLabel?.message
                          : ''}
                      </div>
                    </div>
                  );
                case 'phoneNumber':
                  return (
                    <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-gray-800 dark:text-gray-100">
                        {renderRequiredLabel(field.label, field.required)}
                      </label>
                      <div className="flex gap-2 w-full h-[48px]">
                        <div className="w-1/3">
                          <Controller
                            name={`${name}.${index}.type`}
                            control={control}
                            defaultValue={value?.type}
                            render={({ field: selectField }) => (
                              <Select
                                value={selectField.value}
                                onValueChange={selectField.onChange}
                                disabled={field?.disabled || false}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Type" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value={PhoneNumberType.MOBILE}>
                                    Mobile
                                  </SelectItem>
                                  <SelectItem value={PhoneNumberType.HOME}>
                                    Home
                                  </SelectItem>
                                  <SelectItem value={PhoneNumberType.WORK}>
                                    Work
                                  </SelectItem>
                                  <SelectItem value={PhoneNumberType.CALLER}>
                                    Caller
                                  </SelectItem>
                                  <SelectItem value={PhoneNumberType.OTHER}>
                                    Other
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </div>
                        <div className="w-2/3">
                          <Controller
                            name={`${name}.${index}.phoneNumber`}
                            control={control}
                            defaultValue={value?.phoneNumber}
                            render={({ field: inputField }) => (
                              <Input
                                type="tel"
                                ref={withMask('(999) 999-9999', {
                                  autoUnmask: true,
                                })}
                                value={inputField.value?.replace(/^\+1/, '')}
                                onChange={(e) =>
                                  inputField.onChange(
                                    `+1${e.target.value.replace(/\D/g, '')}`,
                                  )
                                }
                                placeholder="(___) ___-____"
                                className="w-full"
                                disabled={field?.disabled || false}
                              />
                            )}
                          />
                        </div>
                      </div>
                      {get(errors, name) && (
                        <div className="text-sm text-red-500">
                          {get(errors, name)?.message}
                          {get(errors, name)?.[index]?.phoneNumber?.message
                            ? get(errors, name)?.[index]?.phoneNumber?.message
                            : ''}
                        </div>
                      )}
                    </div>
                  );
                default:
                  return (
                    <Input
                      type={field.type}
                      value={value as string | number}
                      onChange={onChange}
                      className="w-full"
                      placeholder={field.placeholder}
                      disabled={field?.disabled || false}
                    />
                  );
              }
            }}
          />
          {field.additionalLabel && !field?.disabled && (
            <button
              type="button"
              onClick={() => {
                if (field?.disabled) return;
                remove(index);
              }}
              className={cn(
                'text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 mt-[10px]',
                {
                  'text-gray-400 hover:text-gray-400 dark:text-gray-500 cursor-not-allowed':
                    field?.disabled,
                  'mt-[-7px]':
                    get(errors, name)?.[index]?.phoneNumber?.message ||
                    get(errors, name)?.[index]?.addressLabel?.message,
                },
              )}
            >
              <IcDeleteTrash />
            </button>
          )}
        </div>
      ))}

      {field.additionalLabel && !field?.disabled ? (
        <div className="text-sm py-1">
          <Button
            color="error"
            type="button"
            size="sm"
            variant="secondary"
            className="w-fit text-sm"
            onClick={() => append(getDefaultValue())}
          >
            + Add additional {field.additionalLabel?.toLowerCase()}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
